'use client';
import { useState } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { Loader2, Mail, X } from 'lucide-react';
import type { PendingInvitation } from '@/lib/api';
import { acceptPendingTeamInvitation, declinePendingTeamInvitation } from '@/lib/api';
import { useBillingInfoStore } from '@/store/billingInfoStore';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getPendingTeamInvitationDescription, getPendingTeamInvitationSummary } from '@/lib/pendingTeamInvitationPanel';
interface PendingTeamInvitationsPanelProps {
    invitations: PendingInvitation[];
    isLoading: boolean;
    error: Error | null;
    onClose: () => void;
    onRefresh: () => Promise<void>;
}
type PendingAction = {
    id: string;
    action: 'accept' | 'decline';
} | null;
export function PendingTeamInvitationsPanel({ invitations, isLoading, error, onClose, onRefresh }: PendingTeamInvitationsPanelProps) {
    const t = useTranslations$1();
    const [pendingAction, setPendingAction] = useState<PendingAction>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const handleAccept = async (invitation: PendingInvitation)=>{
        setPendingAction({
            id: invitation.id,
            action: 'accept'
        });
        setActionError(null);
        try {
            const result = await acceptPendingTeamInvitation(invitation.id);
            await Promise.all([
                onRefresh(),
                useBillingInfoStore.getState().forceFetch()
            ]);
            onClose();
            window.location.href = result.teamSlug ? `/team/${encodeURIComponent(result.teamSlug)}/${result.role === 'billing' ? 'billing' : 'welcome'}` : '/projects';
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to accept invitation');
        } finally{
            setPendingAction(null);
        }
    };
    const handleDecline = async (invitation: PendingInvitation)=>{
        setPendingAction({
            id: invitation.id,
            action: 'decline'
        });
        setActionError(null);
        try {
            await declinePendingTeamInvitation(invitation.id);
            await Promise.all([
                onRefresh(),
                useBillingInfoStore.getState().forceFetch()
            ]);
        } catch (err) {
            setActionError(err instanceof Error ? err.message : 'Failed to decline invitation');
        } finally{
            setPendingAction(null);
        }
    };
    return (<div className="px-3 py-3 bg-primary/5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Mail className="w-4 h-4 text-primary"/>
            {getPendingTeamInvitationSummary(invitations.length)}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{getPendingTeamInvitationDescription(invitations)}</p>
        </div>
        <button type="button" onClick={onClose} className="rounded p-0.5 text-muted-foreground hover:bg-secondary/50 hover:text-foreground" aria-label={t("RYb62i")}>
          <X className="w-3.5 h-3.5"/>
        </button>
      </div>

      {isLoading ? (<div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin"/>
          {t("JD5R7e")}
        </div>) : error ? (<div className="mt-3 text-xs text-destructive">{error.message}</div>) : invitations.length === 0 ? (<div className="mt-3 text-xs text-muted-foreground">{t("0B8kWo")}</div>) : (<div className="mt-3 space-y-2">
          {invitations.map((invitation)=>{
        const isAccepting = pendingAction?.id === invitation.id && pendingAction?.action === 'accept';
        const isDeclining = pendingAction?.id === invitation.id && pendingAction?.action === 'decline';
        const isBusy = pendingAction != null;
        return (<div key={invitation.id} className="rounded-md border border-border bg-popover/70 p-3">
                <div className="text-sm font-medium text-foreground">{invitation.teamName || t("bAsgbr")}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {t("aa7saC", {
            name: invitation.invitedByAlias || t("2-zQlP"),
            role: invitation.role
        })}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button size="sm" className="h-7 px-2.5" disabled={isBusy} onClick={()=>handleAccept(invitation)}>
                    {isAccepting && <Loader2 className="w-3.5 h-3.5 animate-spin"/>}
                    {t("sjzLbX")}
                  </Button>
                  <Button size="sm" variant="ghost" className={cn('h-7 px-2.5 text-muted-foreground', isDeclining && 'text-foreground')} disabled={isBusy} onClick={()=>handleDecline(invitation)}>
                    {isDeclining && <Loader2 className="w-3.5 h-3.5 animate-spin"/>}
                    {t("pvtgR2")}
                  </Button>
                </div>
              </div>);
    })}
        </div>)}

      {actionError && <div className="mt-3 text-xs text-destructive">{actionError}</div>}
    </div>);
}
